import {
  CalendarDaysIcon,
  ChartBarIcon,
  ExclamationCircleIcon,
  SwatchIcon,
} from "@heroicons/react/24/outline";
import { ChevronRightIcon, TableIcon } from "@radix-ui/react-icons";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CompletedProjectsDistributionData,
  useGetAverageProjectCompletionTime,
} from "src/api/Analytics/project";
import { ProjectsV2FilterGroup } from "src/api/Services/Projects/projects";
import { useGetJourneyTemplates } from "src/api/Services/Tasks/journeys";
import { useGetTaskersForInstance } from "src/api/Services/Tasks/tasks";
import { useGetSpaces } from "src/api/Spaces/spaces";
import { Button } from "src/components/ui/button";
import { ComboChart } from "src/components/ui/combo-chart";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "src/components/ui/dialog";
import InputHeading from "src/components/ui/Headings/InputHeading";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/components/ui/hover-card";
import { Loading } from "src/components/ui/loading";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "src/components/ui/select";
import { clean, Filter } from "src/pages/spaces/space/projects";
import { getAverageCompletionTimeFilterKey } from "src/utils/filters";

export const useOnWindowResize = (handler: { (): void }) => {
  React.useEffect(() => {
    const handleResize = () => {
      handler();
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [handler]);
};

type Props = {};

type ProjectData = {
  _id: string;
  name: string;
  startDate?: string;
  createdAt: string;
  closedTasks: number;
  totalTasks: number;
};

type GroupBy = "none" | "owner";

type Timescale = "month" | "quarter";

type View = "chart" | "table";

export default function AverageCompletionTime({}: Props) {
  const [filterGroup, setFilterGroup] = useState<ProjectsV2FilterGroup>(() => {
    const savedFilterGroup = localStorage.getItem(
      getAverageCompletionTimeFilterKey()
    );
    if (savedFilterGroup) {
      return JSON.parse(savedFilterGroup);
    }
    return {
      logic: "AND",
      children: [],
    };
  });
  useEffect(() => {
    if (filterGroup.children.length === 0) {
      localStorage.removeItem(getAverageCompletionTimeFilterKey());
    } else {
      localStorage.setItem(
        getAverageCompletionTimeFilterKey(),
        JSON.stringify(filterGroup)
      );
    }
  }, [filterGroup]);
  const { data: instanceMembers } = useGetTaskersForInstance();
  const { data: spaces } = useGetSpaces();

  const { data: projects, isLoading } = useGetAverageProjectCompletionTime(
    {
      logic: "AND",
      children: [
        {
          field: "project-status",
          comparator: "in",
          value: ["Completed"],
        },
      ],
    },
    clean(filterGroup)
  );

  const [startDateEndFilter, setStartDateEndFilter] = useState(new Date());
  const [startDateStartFilter, setStartDateStartFilter] = useState(
    new Date(startDateEndFilter.getTime() - 1000 * 60 * 60 * 24 * 365 * 1)
  );

  const projectCompletionData = useMemo(
    () =>
      projects
        ?.filter(
          (project) =>
            project.assignedStartDate &&
            project.keyDates.completionDate &&
            new Date(project.assignedStartDate) >= startDateStartFilter &&
            new Date(project.keyDates.completionDate) <= startDateEndFilter &&
            new Date(project.keyDates.completionDate) >
              new Date(project.assignedStartDate)
        )
        .map((project) => {
          const startDate = new Date(project.assignedStartDate);
          const endDate = new Date(project.keyDates.completionDate);
          const days = dayjs(endDate).diff(startDate, "days");

          return {
            _id: project._id,
            startDate: project.assignedStartDate,
            name: project.name,
            days: days,
          };
        }) ?? [],
    [projects]
  );

  const chartData = useMemo(() => {
    const data = projectCompletionData.reduce((acc, project) => {
      const month = new Date(project.startDate).toLocaleString("default", {
        month: "short",
        year: "2-digit",
      });
      if (!acc[month]) {
        acc[month] = {
          date: month,
          "Average Completion Time": 0,
          "Number of Projects Started": 0,
        };
      }
      acc[month]["Number of Projects Started"] += 1;
      acc[month]["Average Completion Time"] += project.days;
      return acc;
    }, {} as Record<string, { date: string; "Average Completion Time": number; "Number of Projects Started": number }>);

    const monthNames = [];
    let date = new Date(startDateStartFilter);
    while (date <= startDateEndFilter) {
      monthNames.push(
        date.toLocaleString("default", {
          month: "short",
          year: "2-digit",
        })
      );
      date = dayjs(date).add(1, "month").toDate();
    }

    return monthNames.map((month) => {
      const dataItem = data[month];
      return {
        date: month,
        "Average Completion Time": dataItem
          ? dataItem["Average Completion Time"] /
            dataItem["Number of Projects Started"]
          : 0,
        "Number of Projects Started": dataItem
          ? dataItem["Number of Projects Started"]
          : 0,
      };
    });
  }, [projectCompletionData]);
  // const chartData = useMemo(() => {
  //   const data = projectCompletionData.reduce((acc, project) => {
  //     const month = new Date(project.createdAt).toLocaleString("default", {
  //       month: "short",
  //     });
  //     if (!acc[month]) {
  //       acc[month] = { date: month, "Average Completion Time": 0, "Number of Projects": 0 };
  //     }
  //     acc[month]["Number of Projects"] += 1;
  //     acc[month]["Average Completion Time"] += project.days;
  //     return acc;
  //   }, {} as Record<string, { date: string; "Average Completion Time": number; "Number of Projects": number }>);
  //   return Object.values(data).map((item) => ({
  //     date: item.date,
  //     "Average Completion Time": item["Average Completion Time"] / item["Number of Projects"],
  //     "Number of Projects": item["Number of Projects"],
  //   }));
  // }, [projectCompletionData]);

  const projectCount = useMemo(() => {
    return projectCompletionData.length;
  }, [chartData]);
  const [monthBucket, setMonthBucket] = useState<string | null>(null);
  const { journeyTemplates } = useGetJourneyTemplates();

  const filteredProjects = useMemo(() => {
    if (!monthBucket) {
      return (
        projects?.filter(
          (project) =>
            project.assignedStartDate &&
            project.keyDates.completionDate &&
            new Date(project.assignedStartDate) >= startDateStartFilter &&
            new Date(project.keyDates.completionDate) <= startDateEndFilter &&
            new Date(project.assignedStartDate) <
              new Date(project.keyDates.completionDate)
        ) ?? []
      );
    } else {
      return (
        projects?.filter(
          (project) => {
            const startDate = new Date(project.assignedStartDate);
            const endDate = new Date(project.keyDates.completionDate);
            if (!startDate || !endDate) {
              return false;
            }
            if (startDate > endDate) {
              return false;
            }
            const month = new Date(startDate).toLocaleString("default", {
              month: "short",
              year: "2-digit",
            });
            if (
              monthBucket ===
              new Date(startDate).toLocaleString("default", {
                month: "short",
                year: "2-digit",
              })
            ) {
              return true;
            }
            return false;
          }
          // (project.assignedStartDate || project.createdAt) &&
          // project.keyDates.completionDate &&
          // new Date(project.keyDates.completionDate).toLocaleString(
          //   "default",
          //   {
          //     month: "short",
          //     year: "2-digit",
          //   }
          // ) === monthBucket
        ) ?? []
      );
    }
  }, [projects, monthBucket]);

  const filteredProjectsInLast3Months = useMemo(() => {
    const last3MonthsNames: string[] = [];
    const date = new Date(startDateEndFilter);
    date.setMonth(date.getMonth() - 1);
    while (last3MonthsNames.length < 3) {
      last3MonthsNames.push(
        date.toLocaleString("default", {
          month: "short",
          year: "2-digit",
        })
      );
      date.setMonth(date.getMonth() - 1);
    }
    return (
      filteredProjects?.filter(
        (project) =>
          project.assignedStartDate &&
          last3MonthsNames.includes(
            new Date(project.assignedStartDate).toLocaleString("default", {
              month: "short",
              year: "2-digit",
            })
          )
      ) ?? []
    );
  }, [filteredProjects, startDateEndFilter, startDateStartFilter]);

  const keyDataLast3Months = useMemo(() => {
    return filteredProjectsInLast3Months
      .map((project) => {
        const startDate = new Date(project.assignedStartDate);
        const endDate = new Date(project.keyDates.completionDate);
        const days = dayjs(endDate).diff(startDate, "days");
        return {
          _id: project._id,
          startDate: project.assignedStartDate,
          name: project.name,
          days: days,
        };
      })
      .sort((a, b) => b.days - a.days);
  }, [filteredProjectsInLast3Months]);

  const filteredProjectsInLast12Months = useMemo(() => {
    const last12MonthsNames: string[] = [];
    const date = new Date(startDateEndFilter);
    date.setMonth(date.getMonth() - 1);
    while (last12MonthsNames.length < 12) {
      last12MonthsNames.push(
        date.toLocaleString("default", {
          month: "short",
          year: "2-digit",
        })
      );
      date.setMonth(date.getMonth() - 1);
    }
    return (
      filteredProjects?.filter(
        (project) =>
          project.assignedStartDate &&
          last12MonthsNames.includes(
            new Date(project.assignedStartDate).toLocaleString("default", {
              month: "short",
              year: "2-digit",
            })
          )
      ) ?? []
    );
  }, [filteredProjects, startDateEndFilter, startDateStartFilter]);

  const keyDataLast12Months = useMemo(() => {
    return filteredProjectsInLast12Months
      .map((project) => {
        const startDate = new Date(project.assignedStartDate);
        const endDate = new Date(project.keyDates.completionDate);
        const days = dayjs(endDate).diff(startDate, "days");
        return {
          _id: project._id,
          startDate: project.assignedStartDate,
          name: project.name,
          days: days,
        };
      })
      .sort((a, b) => b.days - a.days);
  }, [filteredProjectsInLast12Months]);

  const [key, setKey] = useState(0);
  const [invalidProjectsDialog, setInvalidProjectsDialog] = useState(false);

  const projectsFilteredOut = useMemo(() => {
    return (
      projects?.filter((project) => {
        if (!project.assignedStartDate) {
          return true;
        } else {
          const startDate = new Date(project.assignedStartDate);
          const endDate = new Date(project.keyDates.completionDate);
          return startDate > endDate;
        }
      }) ?? []
    );
  }, [projects]);

  const [groupBy, setGroupBy] = useState<GroupBy>("none");
  const [timescale, setTimescale] = useState<Timescale>("month");
  const [view, setView] = useState<View>("chart");

  const projectOwners = useMemo(() => {
    const owners: { _id: string; name: string }[] = [];
    projects?.forEach((p) => {
      if (!owners.some((o) => o._id === p.owner._id)) {
        owners.push({
          _id: p.owner._id,
          name: p.owner.name,
        });
      }
    });
    return owners;
  }, [projects]);

  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 shrink-0 items-center border-b border-gray-200/70 px-8 text-sm text-gray-800">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/analytics`}>
            <div className="text-gray-800">{"Analytics"}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">
            {"Average Project Completion Time"}
          </div>
          {/* <Button variant={"ghost"}>
            <InformationCircleIcon className="h-4 w-4 text-gray-500" />
          </Button> */}
        </div>
      </div>
      <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-gray-200/70 px-8">
        <div className="flex items-center gap-2">
          <Filter
            filterGroup={filterGroup}
            setFilterGroup={setFilterGroup}
            isLoading={!instanceMembers}
            filterModels={[
              {
                field: "project-owner",
                projectOwners:
                  instanceMembers?.map((m) => {
                    return {
                      _id: m._id,
                      name: m.name,
                      email: m.email,
                    };
                  }) ?? [],
              },
              {
                field: "space",
                spaces:
                  spaces?.map((s) => ({
                    _id: s._id,
                    name: s.name,
                    color: s.color,
                  })) ?? [],
              },
              {
                field: "project-template",
                projectTemplates:
                  journeyTemplates?.map((jt) => {
                    return {
                      _id: jt._id?.toString() ?? "",
                      name: jt.name,
                    };
                  }) ?? [],
              },
            ]}
          />
          <GroupBySelect groupBy={groupBy} setGroupBy={setGroupBy} />
          <TimescaleSelect timescale={timescale} setTimescale={setTimescale} />
          {/* <ViewSelect view={view} setView={setView} /> */}
        </div>

        <div className="flex items-center gap-2">
          {projectsFilteredOut.length > 0 && (
            <Button
              variant={"secondary"}
              className="border-dashed border-red-500 font-normal text-red-400"
              onClick={() => {
                setInvalidProjectsDialog(true);
              }}
            >
              <ExclamationCircleIcon className="mr-1.5 h-[18px] w-[18px]" />
              {projectsFilteredOut.length} Invalid{" "}
              {`Project${projectsFilteredOut.length === 1 ? "" : "s"}`}
            </Button>
          )}
        </div>
      </div>
      <Dialog
        open={invalidProjectsDialog}
        onOpenChange={setInvalidProjectsDialog}
      >
        <DialogContent className="max-w-2xl px-0">
          <DialogTitle className="px-6">Invalid Projects</DialogTitle>
          <DialogDescription className="px-6">
            Projects are invalid if they are missing a start date or if the
            start date is after the completion date.
          </DialogDescription>
          <div className="mt-4">
            <div className="overflow-auto">
              <div className="mx-6 mb-1 flex items-center gap-3 border-b pb-2">
                <div className="flex-1">
                  <InputHeading heading="Project" />
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <div className="w-32">
                    <InputHeading heading="Start Date" />
                  </div>
                  <div className="w-32">
                    <InputHeading heading="Completion Date" />
                  </div>
                </div>
              </div>
              {projectsFilteredOut.map((project) => (
                <div
                  key={project._id}
                  className="mx-4 flex h-8 cursor-pointer items-center gap-3 rounded-md px-2 hover:bg-gray-100"
                  onClick={() => {
                    navigate(`/projects/${project._id}`);
                  }}
                >
                  <div className="flex-1 truncate text-sm font-medium">
                    {project.name}
                  </div>
                  <div className="w-32 text-sm font-light text-gray-600">
                    {project.assignedStartDate ? (
                      <div
                        className={clsx(
                          project.assignedStartDate >
                            project.keyDates.completionDate
                            ? "text-red-500"
                            : "text-gray-600"
                        )}
                      >
                        {dayjs(project.assignedStartDate).format(
                          "DD MMM, YYYY"
                        )}
                      </div>
                    ) : (
                      <div className="text-red-500">Missing</div>
                    )}
                  </div>
                  <div className="w-32 text-sm font-light text-gray-600">
                    <>
                      {dayjs(project.keyDates.completionDate).format(
                        "DD MMM, YYYY"
                      )}
                    </>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <div className="flex-1 overflow-auto bg-gray-50 py-10">
        {!isLoading ? (
          <>
            {view === "chart" && (
              <>
                {!!projects?.length ? (
                  <>
                    {groupBy === "none" && (
                      <ChartCard
                        title="All Projects"
                        projects={projects ?? []}
                        timescale={timescale}
                        startDateStartFilter={startDateStartFilter}
                        startDateEndFilter={startDateEndFilter}
                      />
                    )}
                    {groupBy === "owner" && (
                      <div className="space-y-4">
                        {projectOwners.map((owner) => (
                          <ChartCard
                            key={owner._id}
                            title={owner.name}
                            projects={
                              projects?.filter(
                                (p) => p.owner._id === owner._id
                              ) ?? []
                            }
                            timescale={timescale}
                            startDateStartFilter={startDateStartFilter}
                            startDateEndFilter={startDateEndFilter}
                          />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {filterGroup.children.length > 0 ? (
                      <div className="mx-auto mt-20 w-fit">
                        No completed projects meet the filter criteria
                      </div>
                    ) : (
                      <div className="mx-auto mt-20 w-fit">
                        No projects have been completed as yet
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <Loading className="mx-auto mt-[20vh]" />
        )}
      </div>
      {/* <div className="flex h-full flex-col overflow-auto">
        <div className="flex flex-1 flex-col border-b border-gray-200 bg-gray-50 py-8 px-8">
          {!!projects ? (
            <div className="mx-auto w-full max-w-5xl rounded-lg border border-gray-200 bg-white p-6 px-8 shadow-sm">
              <div className="mb-6 text-base font-normal">All Projects</div>
              <div className="flex w-full items-stretch justify-center gap-20">
                <div className="flex h-full flex-col space-y-4">
                  <div className="space-y-4 ">
                    <div className="space-y-2">
                      <div className="flex w-72 flex-col gap-1 rounded-md border border-gray-200/70 p-4 shadow-sm">
                        <div className="w-48 text-xs text-gray-700">
                          Number of projects
                        </div>
                        <div className="text-xl font-medium">
                          {filteredProjectsInLast12Months.length}
                        </div>
                      </div>
                      <div className="flex w-72 flex-col gap-1 rounded-md border border-gray-200/70 p-4 shadow-sm">
                        <div className="w-48 text-xs text-gray-700">
                          Average Completion Time
                        </div>
                        <div className="text-xl font-medium">
                          {filteredProjectsInLast12Months.length > 0
                            ? Math.round(
                                keyDataLast12Months.reduce(
                                  (acc, project) => acc + project.days,
                                  0
                                ) / filteredProjectsInLast12Months.length
                              ) + " days"
                            : "N/A"}
                        </div>
                      </div>
                      <div className="flex w-72 flex-col gap-1 rounded-md border border-gray-200/70 p-4 shadow-sm">
                        <div className="w-48 text-xs text-gray-700">
                          Median Completion Time
                        </div>
                        <div className="text-xl font-medium">
                          {filteredProjectsInLast12Months.length > 0 ? (
                            <>
                              {keyDataLast12Months.length % 2 === 0
                                ? Math.round(
                                    (keyDataLast12Months[
                                      keyDataLast12Months.length / 2 - 1
                                    ].days +
                                      keyDataLast12Months[
                                        keyDataLast12Months.length / 2
                                      ].days) /
                                      2
                                  ) + " days"
                                : keyDataLast12Months[
                                    Math.floor(keyDataLast12Months.length / 2)
                                  ].days + " days"}
                            </>
                          ) : (
                            "N/A"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-96 space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="text-xs font-light text-gray-600">
                    Last 12 months
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-6">
                      <div className="w-48 text-sm text-gray-700">
                        Number of projects
                      </div>
                      <div className="text-sm font-medium">
                        {filteredProjectsInLast12Months.length}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="w-48 text-sm text-gray-700">
                        Average Completion Time
                      </div>
                      <div className="text-sm font-medium">
                        {filteredProjectsInLast12Months.length > 0
                          ? Math.round(
                              keyDataLast12Months.reduce(
                                (acc, project) => acc + project.days,
                                0
                              ) / filteredProjectsInLast12Months.length
                            ) + " days"
                          : "N/A"}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="w-48 text-sm text-gray-700">
                        Median Completion Time
                      </div>
                      <div className="text-sm font-medium">
                        {filteredProjectsInLast12Months.length > 0 ? (
                          <>
                            {keyDataLast12Months.length % 2 === 0
                              ? Math.round(
                                  (keyDataLast12Months[
                                    keyDataLast12Months.length / 2 - 1
                                  ].days +
                                    keyDataLast12Months[
                                      keyDataLast12Months.length / 2
                                    ].days) /
                                    2
                                ) + " days"
                              : keyDataLast12Months[
                                  Math.floor(keyDataLast12Months.length / 2)
                                ].days + " days"}
                          </>
                        ) : (
                          "N/A"
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                  <div className="w-96 flex-1 space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="text-xs font-light text-gray-600">
                    Last 3 months vs Previous 3 months
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-6">
                      <div className="w-48 text-sm">Number of projects</div>
                      <div className="text-sm font-medium">
                        {filteredProjectsInLast3Months.length}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="w-48 text-sm">
                        Average Completion Time
                      </div>
                      <div className="text-sm font-medium">
                        {filteredProjectsInLast3Months.length > 0
                          ? Math.round(keyDataLast3Months.reduce(
                              (acc, project) => acc + project.days,
                              0
                            ) /
                              filteredProjectsInLast3Months.length) +
                            " days"
                          : "N/A"}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="w-48 text-sm">Median Completion Time</div>
                      <div className="text-sm font-medium">
                        {filteredProjectsInLast3Months.length > 0 ? (
                          <>
                            {keyDataLast3Months.length % 2 === 0
                              ? Math.round((keyDataLast3Months[
                                  keyDataLast3Months.length / 2 - 1
                                ].days +
                                  keyDataLast3Months[
                                    keyDataLast3Months.length / 2
                                  ].days) /
                                  2) +
                                " days"
                              : keyDataLast3Months[
                                  Math.floor(keyDataLast3Months.length / 2)
                                ].days + " days"}
                          </>
                        ) : (
                          "N/A"
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                </div>
                <div className="h-full w-20 bg-white p-4"></div>

                {monthBucket && (
                  <Button
                    variant="secondary"
                    className="absolute right-4 top-4"
                    onClick={() => {
                      setKey((prev) => prev + 1);
                      setMonthBucket(null);
                    }}
                  >
                    Clear selection
                  </Button>
                )}

                {isLoading ? (
                  <Loading />
                ) : (
                  <>
                    {projectCount > 0 ? (
                      <ComboChart
                        key={key}
                        // defaultValue={monthBucket}
                        data={chartData}
                        className="h-[280px]"
                        // className="h-80"
                        index="date"
                        enableBiaxial={true}
                        barSeries={{
                          categories: ["Average Completion Time"],
                          yAxisLabel: "Average Completion Time",
                          valueFormatter: (value) =>
                            `${Math.round(value)} days`,
                          yAxisWidth: 80,
                        }}
                        lineSeries={{
                          categories: ["Number of Projects Started"],
                          showYAxis: true,
                          yAxisLabel: "Number of Projects Started",
                          colors: ["amber"],
                          yAxisWidth: 60,
                          allowDecimals: false,
                          valueFormatter: (value) => `${value}`,
                        }}
                        onValueChange={(value) => {
                          console.log(value);
                          if (value?.date) {
                            setMonthBucket(value?.date as string);
                          } else {
                            setMonthBucket(null);
                          }
                        }}
                      />
                    ) : (
                      <div className="my-auto flex h-full w-full items-center justify-center text-center text-sm font-light text-gray-600">
                        <div>No data available</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <Loading className="mx-auto mt-[20vh]" />
          )}
        </div>
        <div>
          <ProjectsTable projects={filteredProjects} />
        </div>
      </div> */}
    </div>
  );
}

function ChartCard({
  title,
  projects,
  timescale,
  startDateStartFilter,
  startDateEndFilter,
}: {
  title: string;
  projects: CompletedProjectsDistributionData[];
  timescale: Timescale;
  startDateStartFilter: Date;
  startDateEndFilter: Date;
}) {
  const projectCompletionData = useMemo(
    () =>
      projects
        ?.filter(
          (project) =>
            project.assignedStartDate &&
            project.keyDates.completionDate &&
            new Date(project.assignedStartDate) >= startDateStartFilter &&
            new Date(project.keyDates.completionDate) <= startDateEndFilter &&
            new Date(project.keyDates.completionDate) >
              new Date(project.assignedStartDate)
        )
        .map((project) => {
          const startDate = new Date(project.assignedStartDate);
          const endDate = new Date(project.keyDates.completionDate);
          const days = dayjs(endDate).diff(startDate, "days");

          return {
            _id: project._id,
            startDate: project.assignedStartDate,
            name: project.name,
            days: days,
          };
        }) ?? [],
    [projects]
  );

  const chartData = useMemo(() => {
    if (timescale === "month") {
      const data = projectCompletionData.reduce((acc, project) => {
        const month = new Date(project.startDate).toLocaleString("default", {
          month: "short",
          year: "2-digit",
        });
        if (!acc[month]) {
          acc[month] = {
            date: month,
            "Average Completion Time": 0,
            "Number of Projects Started": 0,
          };
        }
        acc[month]["Number of Projects Started"] += 1;
        acc[month]["Average Completion Time"] += project.days;
        return acc;
      }, {} as Record<string, { date: string; "Average Completion Time": number; "Number of Projects Started": number }>);

      const monthNames = [];
      let date = new Date(startDateStartFilter);
      while (date <= startDateEndFilter) {
        monthNames.push(
          date.toLocaleString("default", {
            month: "short",
            year: "2-digit",
          })
        );
        date = dayjs(date).add(1, "month").toDate();
      }

      return monthNames.map((month) => {
        const dataItem = data[month];
        return {
          date: month,
          "Average Completion Time": dataItem
            ? dataItem["Average Completion Time"] /
              dataItem["Number of Projects Started"]
            : 0,
          "Number of Projects Started": dataItem
            ? dataItem["Number of Projects Started"]
            : 0,
        };
      });
    } else {
      const last4Quarters = getDatesOfLast4QuartersIncludingCurrent();
      return last4Quarters.map((quarter) => {
        const startDate = quarter.start;
        const endDate = quarter.end;
        const projects = projectCompletionData.filter(
          (project) =>
            new Date(project.startDate) >= startDate &&
            new Date(project.startDate) <= endDate
        );
        const averageCompletionTime =
          projects.length > 0
            ? projects.reduce((acc, project) => acc + project.days, 0) /
              projects.length
            : 0;
        return {
          date: quarter.quarter,
          "Average Completion Time": averageCompletionTime,
          "Number of Projects Started": projects.length,
        };
      });
    }
  }, [projectCompletionData, timescale]);
  return (
    <div className="mx-auto w-full max-w-5xl rounded-lg border border-gray-200 bg-white p-6 px-8 shadow-sm">
      <div className="mb-6 w-fit rounded-md border border-gray-200/70 bg-gray-100 px-2 py-1 text-sm font-normal shadow-sm">
        {title}
      </div>
      <div className="flex w-full items-stretch justify-center gap-20">
        <div className="flex h-full flex-col space-y-4">
          <div className="space-y-4 ">
            <div className="space-y-2">
              <div className="flex w-72 flex-col gap-1 rounded-md border border-gray-200/70 p-4 shadow-sm">
                <div className="w-48 text-xs text-gray-700">
                  Number of projects
                </div>
                <div className="text-xl font-medium">
                  {projectCompletionData.length}
                </div>
              </div>
              <div className="flex w-72 flex-col gap-1 rounded-md border border-gray-200/70 p-4 shadow-sm">
                <div className="w-48 text-xs text-gray-700">
                  Average Completion Time
                </div>
                <div className="text-xl font-medium">
                  {projectCompletionData.length > 0
                    ? Math.round(
                        projectCompletionData.reduce(
                          (acc, project) => acc + project.days,
                          0
                        ) / projectCompletionData.length
                      ) + " days"
                    : "N/A"}
                </div>
              </div>
              <div className="flex w-72 flex-col gap-1 rounded-md border border-gray-200/70 p-4 shadow-sm">
                <div className="w-48 text-xs text-gray-700">
                  Median Completion Time
                </div>
                <div className="text-xl font-medium">
                  {projectCompletionData.length > 0 ? (
                    <>
                      {projectCompletionData.length % 2 === 0
                        ? Math.round(
                            (projectCompletionData[
                              projectCompletionData.length / 2 - 1
                            ].days +
                              projectCompletionData[
                                projectCompletionData.length / 2
                              ].days) /
                              2
                          ) + " days"
                        : projectCompletionData[
                            Math.floor(projectCompletionData.length / 2)
                          ].days + " days"}
                    </>
                  ) : (
                    "N/A"
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* <div className="w-96 space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="text-xs font-light text-gray-600">
                    Last 12 months
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-6">
                      <div className="w-48 text-sm text-gray-700">
                        Number of projects
                      </div>
                      <div className="text-sm font-medium">
                        {filteredProjectsInLast12Months.length}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="w-48 text-sm text-gray-700">
                        Average Completion Time
                      </div>
                      <div className="text-sm font-medium">
                        {filteredProjectsInLast12Months.length > 0
                          ? Math.round(
                              keyDataLast12Months.reduce(
                                (acc, project) => acc + project.days,
                                0
                              ) / filteredProjectsInLast12Months.length
                            ) + " days"
                          : "N/A"}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="w-48 text-sm text-gray-700">
                        Median Completion Time
                      </div>
                      <div className="text-sm font-medium">
                        {filteredProjectsInLast12Months.length > 0 ? (
                          <>
                            {keyDataLast12Months.length % 2 === 0
                              ? Math.round(
                                  (keyDataLast12Months[
                                    keyDataLast12Months.length / 2 - 1
                                  ].days +
                                    keyDataLast12Months[
                                      keyDataLast12Months.length / 2
                                    ].days) /
                                    2
                                ) + " days"
                              : keyDataLast12Months[
                                  Math.floor(keyDataLast12Months.length / 2)
                                ].days + " days"}
                          </>
                        ) : (
                          "N/A"
                        )}
                      </div>
                    </div>
                  </div>
                </div> */}

          {/* <div className="w-96 flex-1 space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="text-xs font-light text-gray-600">
                    Last 3 months vs Previous 3 months
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-6">
                      <div className="w-48 text-sm">Number of projects</div>
                      <div className="text-sm font-medium">
                        {filteredProjectsInLast3Months.length}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="w-48 text-sm">
                        Average Completion Time
                      </div>
                      <div className="text-sm font-medium">
                        {filteredProjectsInLast3Months.length > 0
                          ? Math.round(keyDataLast3Months.reduce(
                              (acc, project) => acc + project.days,
                              0
                            ) /
                              filteredProjectsInLast3Months.length) +
                            " days"
                          : "N/A"}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="w-48 text-sm">Median Completion Time</div>
                      <div className="text-sm font-medium">
                        {filteredProjectsInLast3Months.length > 0 ? (
                          <>
                            {keyDataLast3Months.length % 2 === 0
                              ? Math.round((keyDataLast3Months[
                                  keyDataLast3Months.length / 2 - 1
                                ].days +
                                  keyDataLast3Months[
                                    keyDataLast3Months.length / 2
                                  ].days) /
                                  2) +
                                " days"
                              : keyDataLast3Months[
                                  Math.floor(keyDataLast3Months.length / 2)
                                ].days + " days"}
                          </>
                        ) : (
                          "N/A"
                        )}
                      </div>
                    </div>
                  </div>
                </div> */}
        </div>
        {/* <div className="h-full w-20 bg-white p-4"></div> */}

        {/* {monthBucket && (
                  <Button
                    variant="secondary"
                    className="absolute right-4 top-4"
                    onClick={() => {
                      setKey((prev) => prev + 1);
                      setMonthBucket(null);
                    }}
                  >
                    Clear selection
                  </Button>
                )} */}

        {projectCompletionData.length > 0 ? (
          <ComboChart
            // defaultValue={monthBucket}
            data={chartData}
            className="h-[280px]"
            // className="h-80"
            index="date"
            enableBiaxial={true}
            barSeries={{
              categories: ["Average Completion Time"],
              yAxisLabel: "Average Completion Time",
              valueFormatter: (value) => `${Math.round(value)} days`,
              yAxisWidth: 80,
            }}
            lineSeries={{
              categories: ["Number of Projects Started"],
              showYAxis: true,
              yAxisLabel: "Number of Projects Started",
              colors: ["amber"],
              yAxisWidth: 60,
              allowDecimals: false,
              valueFormatter: (value) => `${value}`,
            }}
            // onValueChange={(value) => {
            //   console.log(value);
            //   if (value?.date) {
            //     setMonthBucket(value?.date as string);
            //   } else {
            //     setMonthBucket(null);
            //   }
            // }}
          />
        ) : (
          <div className="my-auto flex h-full w-full items-center justify-center text-center text-sm font-light text-gray-600">
            <div>No data available</div>
          </div>
        )}
      </div>
    </div>
  );
}

// should return 4 dates, each representing the start and end of a quarter as per regular quarters
// including the current quarter - [{start: "2024-01-01", end: "2024-03-31", quarter: "Q1 2024"}, ...]
function getDatesOfLast4QuartersIncludingCurrent(): {
  start: Date;
  end: Date;
  quarter: string;
}[] {
  const dates: { start: Date; end: Date; quarter: string }[] = [];
  const now = new Date();
  const currentQuarter = Math.floor((now.getMonth() + 3) / 3);
  const currentYear = now.getFullYear();
  for (let i = 0; i < 4; i++) {
    const quarterIndex = currentQuarter - i;
    const year = quarterIndex <= 0 ? currentYear - 1 : currentYear;
    const adjustedQuarter = quarterIndex <= 0 ? quarterIndex + 4 : quarterIndex;

    const startMonth = (adjustedQuarter - 1) * 3;
    const start = new Date(year, startMonth, 1);

    const endMonth = adjustedQuarter * 3;
    const end = new Date(year, endMonth, 0, 23, 59, 59, 999);

    const quarter = `Q${adjustedQuarter} ${year}`;
    dates.push({ start, end, quarter });
  }
  return dates.reverse();
}

function GroupBySelect({
  groupBy,
  setGroupBy,
}: {
  groupBy: GroupBy;
  setGroupBy: (groupBy: GroupBy) => void;
}) {
  return (
    <Select
      value={groupBy}
      onValueChange={(v) => {
        setGroupBy(v as GroupBy);
      }}
    >
      <SelectTrigger
        className={`h-8 w-fit max-w-full gap-0 bg-white py-0 px-2 focus-visible:border-gray-300 focus-visible:ring-0`}
      >
        <div className="flex h-full items-center gap-2 divide-x">
          <div className="inline-flex items-center space-x-1">
            <SwatchIcon className="h-4 w-4" />
            <div className="">Group by</div>
          </div>
          <div className="inline-flex h-full items-center pl-2">
            {groupBy === "none" ? (
              <span className="text-gray-600">None</span>
            ) : (
              <span className="text-gray-600">{"Owner"}</span>
            )}
          </div>
        </div>
      </SelectTrigger>
      <SelectContent className="min-w-[120px]">
        <SelectGroup>
          <SelectItem value="none">None</SelectItem>
          <SelectItem value="owner">Owner</SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function TimescaleSelect({
  timescale,
  setTimescale,
}: {
  timescale: Timescale;
  setTimescale: (timescale: Timescale) => void;
}) {
  return (
    <Select
      value={timescale}
      onValueChange={(v) => {
        setTimescale(v as Timescale);
      }}
    >
      <SelectTrigger
        className={`h-8 w-fit max-w-full gap-0 bg-white py-0 px-2 focus-visible:border-gray-300 focus-visible:ring-0`}
      >
        <div className="flex h-full items-center gap-2 divide-x">
          <div className="inline-flex items-center space-x-1">
            <CalendarDaysIcon className="h-4 w-4" />
            <div className="">Timescale</div>
          </div>
          <div className="inline-flex h-full items-center pl-2">
            <span className="text-gray-600">
              {timescale === "month" ? "Months" : "Quarters"}
            </span>
          </div>
        </div>
      </SelectTrigger>
      <SelectContent className="min-w-[120px]">
        <SelectGroup>
          <SelectItem value="month">Months</SelectItem>
          <SelectItem value="quarter">Quarters</SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function ViewSelect({
  view,
  setView,
}: {
  view: View;
  setView: (view: View) => void;
}) {
  return (
    <Select
      value={view}
      onValueChange={(v) => {
        setView(v as View);
      }}
    >
      <SelectTrigger
        className={`h-8 w-fit max-w-full gap-0 bg-white py-0 px-2 focus-visible:border-gray-300 focus-visible:ring-0`}
      >
        <div className="flex h-full items-center gap-2 divide-x">
          <div className="inline-flex items-center space-x-1">
            {view === "chart" && <ChartBarIcon className="-4 w-4" />}
            {view === "table" && <TableIcon className="-4 w-4" />}
            <div className="">View</div>
          </div>
          <div className="inline-flex h-full items-center pl-2">
            <span className="text-gray-600">
              {view === "chart" ? "Chart" : "Table"}
            </span>
          </div>
        </div>
      </SelectTrigger>
      <SelectContent className="min-w-[120px]">
        <SelectGroup>
          <SelectItem value="chart">Chart</SelectItem>
          <SelectItem value="table">Table</SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function getColumns(): ColumnDef<CompletedProjectsDistributionData>[] {
  return [
    {
      id: "project",
      header: () => {
        return <div className="">Project</div>;
      },
      cell: ({ row }) => {
        return (
          <div className="h-full max-w-[400px] truncate py-3">
            {row.original.name}
          </div>
        );
      },
      accessorFn: ({ name }) => name,
    },
    {
      id: "completionTime",
      header: () => {
        return <div className="">Completion Time</div>;
      },
      accessorFn: (project) => {
        const startDate = dayjs(project.assignedStartDate || project.createdAt);
        const endDate = dayjs(project.keyDates.completionDate);
        return endDate.diff(startDate, "days");
      },
      cell: ({ row }) => {
        const startDate = dayjs(
          row.original.assignedStartDate || row.original.createdAt
        );
        const endDate = dayjs(row.original.keyDates.completionDate);
        const completionTime = endDate.diff(startDate, "days");
        return (
          <HoverCard openDelay={100} closeDelay={100}>
            <HoverCardTrigger>
              <div className="h-full w-fit max-w-[200px] truncate px-6 py-3">
                {completionTime} days
              </div>
            </HoverCardTrigger>
            <HoverCardContent align="center" side="left" sideOffset={12}>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-36 text-sm font-light text-gray-600">
                    Start Date
                  </div>
                  <div className="text-sm font-medium">
                    {dayjs(startDate).format("DD MMM, YYYY")}
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-36 text-sm font-light text-gray-600">
                    Completion Date
                  </div>
                  <div className="text-sm font-medium">
                    {dayjs(endDate).format("DD MMM, YYYY")}
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-36 text-sm font-light text-gray-600">
                    Completion Time
                  </div>
                  <div className="text-sm font-medium">
                    {completionTime} days
                  </div>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        );
      },
    },
    {
      id: "owner",
      header: () => {
        return <div className="">Owner</div>;
      },
      accessorFn: ({ owner }) => owner.name,
      cell: ({ row }) => {
        return (
          <div className="h-full truncate px-6 py-3">
            {row.original.owner.name}
          </div>
        );
      },
    },
  ];
}

const ProjectsTable = ({
  projects,
}: {
  projects: CompletedProjectsDistributionData[];
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable<CompletedProjectsDistributionData>({
    data: projects ?? [],
    columns: getColumns(),
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      // globalFilter: search,
      // columnVisibility: transformColumnsToDisplay(columnsToDisplay),
    },
    // globalFilterFn: fuzzyFilter,
    onSortingChange: setSorting,
    // onGlobalFilterChange: setSearch,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // onColumnVisibilityChange: setColumnsToDisplay,
  });
  const navigate = useNavigate();
  return (
    <div className="w-full flex-1 overflow-auto">
      <div className="max-h-full max-w-full overflow-auto">
        <table
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
          className="w-full"
        >
          <thead className="overflow-auto">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={
                      "sticky top-0 z-10 h-11 whitespace-nowrap border-b border-r border-gray-200/70 bg-gray-50 px-6 text-left text-xs font-normal uppercase tracking-wide text-gray-400 first:left-0 first:z-20 first:pl-8 last:right-0 last:z-20"
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="group relative rounded-lg hover:cursor-pointer hover:bg-gray-50"
                onClick={() => {
                  navigate(`/projects/${row.original._id}`);
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={
                      "whitespace-nowrap border-r border-b border-gray-100 bg-white text-sm text-gray-500 first:sticky first:left-0 first:z-10 first:bg-white first:pr-6 first:font-medium first:text-gray-900 last:sticky last:right-0 last:z-10 group-hover:bg-gray-50 first:group-hover:bg-gray-50 first:sm:pl-8"
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
